// stub 開発モード（X-User-Id）は Clerk 未設定時のフォールバックとして残す。
import type { AiTemplateEditRequest, AiTemplateEditResponse, AiTemplateGenerateRequest, AiTemplateGenerateResponse, AiTemplateImportRequest, AiTemplateImportResponse, FeeCalcInput, FeeCalcResult, FeeSchedule, FeeScheduleDocType } from "@juro/shared";
import { previewMockValues } from "./lib/previewMockValues.js";
import type { TemplateDef } from "@juro/shared";
import { getAuthToken } from "./lib/clerkAuth.js";

export const DEV_USER_ID = "user_amano_akihiro";

export async function authHeaders(extra?: HeadersInit): Promise<HeadersInit> {
  const token = await getAuthToken();
  return {
    "X-User-Id": DEV_USER_ID,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: await authHeaders({ "Content-Type": "application/json", ...init?.headers }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw Object.assign(new Error((body as { error?: { message?: string } }).error?.message ?? `HTTP ${res.status}`), { status: res.status, body });
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/** Blob レスポンス（PDF等）を返すエンドポイント用。request() と同じ認証ヘッダーを付与する。 */
async function requestBlob(path: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(`/api${path}`, { ...init, headers: await authHeaders(init?.headers) });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw Object.assign(new Error((body as { error?: { message?: string } }).error?.message ?? `HTTP ${res.status}`), { status: res.status, body });
  }
  return res;
}

// ── 型定義 ──────────────────────────────────────────────────────────────────

export type ClientType = "corporation" | "individual";

export const CLIENT_TYPE_LABEL: Record<ClientType, string> = {
  corporation: "法人",
  individual:  "個人",
};

export type Client = {
  id: string;
  name: string;
  client_type: ClientType;
  postal_code: string | null;
  address: string | null;
  tel: string | null;
  notes: string | null;
  created_at: string;
};

export type Case = {
  id: string;
  client_id: string;
  client_name: string;
  fiscal_year: number;
  title: string | null;
  status: "active" | "completed" | "cancelled";
  notes: string | null;
  created_at: string;
};

export type CaseDetail = Case & {
  documents: DocumentSummary[];
};

// 見積書↔請求書 連携（estimate-invoice-sync 設計書 §3.1）
export type EstimateSyncStatus = {
  estimate: { id: string; status: "draft" | "issued"; version: number } | null;
  invoice_count: number;
  sync: "no_invoices" | "not_created" | "in_sync" | "stale" | "issued";
};

export type DocumentSummary = {
  id: string;
  doc_type: DocType;
  doc_number: string | null;
  version: number;
  status: DocStatus;
  issued_at: string | null;
  /** 修正版がある場合その新版の id（GET /cases/:id のみ返す。null/undefined = 現行版） */
  superseded_by_id?: string | null;
  adjustment_amount: number;
  notes: string | null;
  created_at: string;
};

export type LineItem = {
  id: string;
  line_order: number;
  // Phase 3（document-type-master-phase3.md）: 資格マスタ統合により、既存3資格固定の
  // ユニオン型から任意の資格キー（tenant_professions 由来のカスタム資格を含む）を許容する
  // string に広げる。
  profession: string | null;
  item_date: string | null;
  description: string;
  fee_amount: number;
  tax_amount: number;
};

export type ComputedTotals = {
  fee_subtotal: number;
  tax_subtotal: number;
  consumption_tax: number;
  withholding_tax: number;
  total: number;
};

export type DocumentDetail = DocumentSummary & {
  case_id: string;
  /** 明細差し替え（見積書の自動同期含む）でも更新される。DocumentPage の state 再初期化キー */
  updated_at: string;
  issued_by: string | null;
  issued_by_name: string | null;
  superseded_by_id: string | null;
  revision_reason: string | null;
  blocks_snapshot: string | null;
  // M1: 宛名上書き・請求日指定（NULL = フォールバック）
  recipient_name: string | null;
  issue_date: string | null;
  /** 書類ごとの発行者上書き（資格者名簿のid。NULL = 既定の資格者） */
  issuer_holder_id: string | null;
  /** 発行時に凍結された発行者情報スナップショット（JSON文字列）。draft のときは null */
  issuer_snapshot_json: string | null;
  line_items: LineItem[];
  computed: ComputedTotals;
  /** 源泉徴収を適用するか（テナントの源泉ルール×顧客種別でサーバーが判定） */
  apply_withholding?: boolean;
  /** 料金表ベースの書類（土地/建物計算書・保証協会請求書）のみ: 構造化計算入力 */
  calc_input?: FeeCalcInput | null;
  /** 料金表ベースの書類のみ: 保存時点の料金表で確定した計算結果スナップショット */
  calc_result?: FeeCalcResult | null;
};

export type BuiltinDocType =
  | "estimate"
  | "judicial_invoice"
  | "administrative_invoice"
  | "surveyor_invoice"
  | "guarantee_invoice"
  | "land_calculation"
  | "building_calculation";

export type DocType = BuiltinDocType | (string & {});

export type DocStatus = "draft" | "issued" | "cancelled";

// ── クライアント API ──────────────────────────────────────────────────────────

export const clientsApi = {
  list: () => request<{ data: Client[]; total: number }>("/clients"),
  get:  (id: string) => request<Client & { cases: Case[] }>(`/clients/${id}`),
  create: (body: Omit<Client, "id" | "created_at">) =>
    request<Client>("/clients", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: Partial<Omit<Client, "id" | "created_at">>) =>
    request<Client>(`/clients/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  delete: (id: string) => request<void>(`/clients/${id}`, { method: "DELETE" }),
};

// ── 案件 API ──────────────────────────────────────────────────────────────────

export const casesApi = {
  list: (params?: { client_id?: string; fiscal_year?: number; status?: string }) => {
    const q = new URLSearchParams();
    if (params?.client_id)   q.set("client_id",   params.client_id);
    if (params?.fiscal_year) q.set("fiscal_year",  String(params.fiscal_year));
    if (params?.status)      q.set("status",       params.status);
    return request<{ data: Case[]; total: number }>(`/cases?${q}`);
  },
  get:    (id: string) => request<CaseDetail>(`/cases/${id}`),
  create: (body: { client_id: string; fiscal_year: number; title?: string; notes?: string }) =>
    request<Case>("/cases", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: { status?: Case["status"]; title?: string; notes?: string }) =>
    request<Case>(`/cases/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  estimateGenerate: (caseId: string, mode: "create" | "refresh" = "create") =>
    request<DocumentDetail>(`/cases/${caseId}/estimate/generate`,
      { method: "POST", body: JSON.stringify({ mode }) }),
  estimateStatus: (caseId: string) =>
    request<EstimateSyncStatus>(`/cases/${caseId}/estimate/status`),
};

// ── 書類 API ──────────────────────────────────────────────────────────────────

export type NewLineItem = {
  line_order: number;
  profession?: string | null;
  item_date?: string;
  description: string;
  fee_amount: number;
  tax_amount: number;
};

export const documentsApi = {
  listByCase: (caseId: string) =>
    request<{ data: DocumentSummary[]; total: number }>(`/cases/${caseId}/documents`),
  get: (id: string) => request<DocumentDetail>(`/documents/${id}`),
  create: (caseId: string, body: {
    doc_type: DocType;
    adjustment_amount?: number;
    notes?: string;
    line_items?: NewLineItem[];
    /** 料金表ベースの書類のみ。指定時はサーバーが再計算して明細を投影する */
    calc_input?: FeeCalcInput;
  }) => request<DocumentDetail>(`/cases/${caseId}/documents`, { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: {
    adjustment_amount?: number;
    notes?: string | null;
    line_items?: NewLineItem[];
    /** 料金表ベースの書類のみ。指定時は明細を投影で全置換（手動 line_items は無視） */
    calc_input?: FeeCalcInput;
    blocks_snapshot?: string;
    // M1: 宛名上書き・請求日（null で列をクリア）
    recipient_name?: string | null;
    issue_date?: string | null;
    /** 書類ごとの発行者上書き（null = 既定の資格者に戻す） */
    issuer_holder_id?: string | null;
  }) => request<DocumentDetail>(`/documents/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  issue:  (id: string) =>
    request<DocumentDetail>(`/documents/${id}/issue`, { method: "POST", body: "{}" }),
  cancel: (id: string, reason: string) =>
    request<DocumentDetail>(`/documents/${id}/cancel`, { method: "POST", body: JSON.stringify({ reason }) }),
  revise: (id: string, revision_reason: string) =>
    request<DocumentDetail>(`/documents/${id}/revise`, { method: "POST", body: JSON.stringify({ revision_reason }) }),

  generatePdf: async (id: string, options?: { force?: boolean }): Promise<Blob> => {
    const suffix = options?.force ? "?force=1" : "";
    const res = await requestBlob(`/documents/${id}/pdf/generate${suffix}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    return res.blob();
  },

  getPdfUrl: (id: string) => `/api/documents/${id}/pdf`,

  /** 発行済み書類の保存済みPDFをR2から取得（再生成しない） */
  getPdf: async (id: string): Promise<Blob> => {
    const res = await requestBlob(`/documents/${id}/pdf`);
    return res.blob();
  },
};

// ── 表示用ラベル ──────────────────────────────────────────────────────────────

export const DOC_TYPE_LABEL: Record<BuiltinDocType, string> = {
  estimate:               "見積書",
  judicial_invoice:       "司法書士請求書",
  administrative_invoice: "行政書士請求書",
  surveyor_invoice:       "調査士請求書",
  guarantee_invoice:      "保証協会請求書",
  land_calculation:       "土地計算書",
  building_calculation:   "建物計算書",
};

export function getDocTypeLabel(docType: string): string {
  return DOC_TYPE_LABEL[docType as BuiltinDocType] ?? docType.replace(/^custom_/, "");
}

export const DOC_STATUS_LABEL: Record<DocStatus, string> = {
  draft:     "下書き",
  issued:    "発行済み",
  cancelled: "取消済み",
};

// ── 料金表 API（土地/建物計算書・保証協会請求書） ──────────────────────────────

export type FeeScheduleResponse = {
  schedule: FeeSchedule;
  source: "custom" | "default";
};

export type FeeScheduleListResponse = {
  data: Record<FeeScheduleDocType, FeeScheduleResponse>;
};

export const feeSchedulesApi = {
  /** 3書類分の料金表をまとめて取得（カスタム or 既定） */
  list: () => request<FeeScheduleListResponse>("/fee-schedules"),
  get: (docType: string) => request<FeeScheduleResponse>(`/fee-schedules/${docType}`),
  /** カスタマイズを保存（UPSERT）。以後に作成する書類から適用される */
  update: (docType: string, schedule: FeeSchedule) =>
    request<FeeScheduleResponse>(`/fee-schedules/${docType}`, {
      method: "PUT",
      body: JSON.stringify({ schedule }),
    }),
  /** カスタマイズを破棄して既定料金表に戻す（論理削除） */
  reset: (docType: string) =>
    request<FeeScheduleResponse>(`/fee-schedules/${docType}`, { method: "DELETE" }),
};

// ── 入金 API ──────────────────────────────────────────────────────────────────

export type Payment = {
  id: string;
  document_id: string;
  paid_date: string;
  bank_name: string | null;
  amount: number;
  memo: string | null;
  created_at: string;
};

export const paymentsApi = {
  listByDocument: (docId: string) =>
    request<{ data: Payment[]; total: number }>(`/documents/${docId}/payments`),
  create: (docId: string, body: { paid_date: string; bank_name?: string; amount: number; memo?: string }) =>
    request<Payment>(`/documents/${docId}/payments`, { method: "POST", body: JSON.stringify(body) }),
  delete: (paymentId: string) =>
    request<void>(`/payments/${paymentId}`, { method: "DELETE" }),
};

export const FISCAL_YEAR_NOW = new Date().getFullYear() % 100;

// ── テンプレート API ──────────────────────────────────────────────────────

export type BlockType =
  | "title" | "text" | "client_info" | "office_info" | "image"
  | "line_items_table" | "totals" | "kv_grid" | "divider" | "spacer"
  | "page_break" | "notes_list";

export type Block = {
  id:            string;
  type:          BlockType;
  visible?:      boolean | string;
  margin_top?:   number;
  margin_bottom?: number;
  config:        Record<string, unknown>;
};

export type FieldDef = {
  key:      string;
  label:    string;
  type:     "text" | "textarea" | "date" | "number" | "currency" | "boolean" | "select";
  required: boolean;
  default?: string | number | boolean | null;
  options?: { value: string; label: string }[];
  help?:    string;
};

export type DocumentTemplate = {
  id:            string;
  tenant_id:     string;
  doc_type:      DocType;
  name:          string;
  description:   string | null;
  schema_version?: number;
  template_json?: TemplateDef;
  blocks:        Block[];
  custom_fields: FieldDef[];
  page_config:   Record<string, unknown>;
  is_default:    number;
  version:       number;
  created_at:    string;
  updated_at:    string;
};

export const BLOCK_TYPE_LABEL: Record<BlockType, string> = {
  title:            "タイトル",
  text:             "テキスト",
  client_info:      "顧客情報",
  office_info:      "事務所情報",
  image:            "画像",
  line_items_table: "明細表",
  totals:           "合計欄",
  kv_grid:          "ラベル・値",
  divider:          "区切り線",
  spacer:           "空白",
  page_break:       "改ページ",
  notes_list:       "注意書き",
};

export const templatesApi = {
  list: (doc_type?: string) => {
    const q = doc_type ? `?doc_type=${doc_type}` : "";
    return request<{ data: DocumentTemplate[]; total: number }>(`/templates${q}`);
  },
  get: (id: string) => request<DocumentTemplate>(`/templates/${id}`),
  getBuiltins: () => request<{ data: { doc_type: string; blocks: Block[] }[] }>("/templates/builtins"),
  create: (body: {
    doc_type: DocType; name: string; description?: string;
    blocks?: Block[]; custom_fields?: FieldDef[]; page_config?: Record<string, unknown>;
    is_default?: boolean; schema_version?: number; template_json?: TemplateDef;
    /** 利用状況計測用: どの作成フロー由来か（省略時はサーバー側で 'blank' 扱い） */
    created_via?: "blank" | "ai" | "pdf_import";
  }) => request<DocumentTemplate>("/templates", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: {
    name?: string; description?: string;
    blocks?: Block[]; custom_fields?: FieldDef[]; page_config?: Record<string, unknown>;
    schema_version?: number; template_json?: TemplateDef;
  }) => request<DocumentTemplate>(`/templates/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  setDefault: (id: string) =>
    request<{ ok: boolean }>(`/templates/${id}/set-default`, { method: "POST", body: "{}" }),
  delete: (id: string) => request<void>(`/templates/${id}`, { method: "DELETE" }),
  listPresets: () => request<{ data: { key: string; doc_type: string; label: string; name: string; template: TemplateDef }[] }>("/templates/presets"),
  installPreset: (key: string) => request<DocumentTemplate>(`/templates/presets/${key}/install`, { method: "POST", body: "{}" }),
  previewUrl: (id: string) => `/api/templates/${id}/preview`,
  // PDF取込の下敷き画像（R2保管）
  uploadAsset: (data: string, content_type: "image/png" | "image/jpeg") =>
    request<{ asset_id: string }>("/templates/assets", {
      method: "POST",
      body: JSON.stringify({ data, content_type }),
    }),
  render: async (template: TemplateDef, signal?: AbortSignal): Promise<{ blob: Blob; blockMapHeader: string | null }> => {
    const res = await requestBlob("/templates/render", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ template, sample_data: previewMockValues, page: 1 }),
      signal,
    });
    return { blob: await res.blob(), blockMapHeader: res.headers.get("X-Block-Map") };
  },
};

// ── テナント設定 API ──────────────────────────────────────────────────────────

export type WithholdingRule = "corporation_only" | "individual_only" | "all" | "none";

export const WITHHOLDING_RULE_LABEL: Record<WithholdingRule, string> = {
  corporation_only: "法人のみ適用",
  individual_only:  "個人のみ適用",
  all:              "全顧客に適用",
  none:             "適用しない",
};

export type TenantSettings = {
  tenant_id:        string;
  withholding_rule: WithholdingRule;
  /** 振込先口座（複数行） */
  bank_info:        string | null;
  /** 既定振込案内文 */
  transfer_note:    string | null;
  updated_at:       string;
};

export const settingsApi = {
  get: () => request<TenantSettings>("/settings"),
  update: (body: { withholding_rule: WithholdingRule; bank_info?: string | null; transfer_note?: string | null }) =>
    request<TenantSettings>("/settings", { method: "PUT", body: JSON.stringify(body) }),
};

// ── テナント（事務所情報）API ──────────────────────────────────────────────────

export type Tenant = {
  id:          string;
  name:        string;
  postal_code: string | null;
  address:     string | null;
  tel:         string | null;
  fax:         string | null;
};

export const tenantsApi = {
  getMe: () => request<Tenant>("/tenants/me"),
  updateMe: (body: { name: string; postal_code?: string | null; address?: string | null; tel?: string | null; fax?: string | null }) =>
    request<Tenant>("/tenants/me", { method: "PUT", body: JSON.stringify(body) }),
};

// ── ユーザー管理 API ──────────────────────────────────────────────────────────
// 利用ユーザー（ログイン・権限・監査）と資格者（帳票に印字される氏名・登録番号）は
// 別エンティティ（docs/design/user-professional-separation.md）。氏名・登録番号の
// 編集は資格者名簿（tenantProfessionsApi の holders 系）で行う。ここでは認証・権限のみを扱う。

export type UserRole = "owner" | "member";

export const USER_ROLE_LABEL: Record<UserRole, string> = {
  owner:  "オーナー",
  member: "メンバー",
};

export type User = {
  id: string;
  name: string;
  email: string;
  role: UserRole;
};

export type NewUser = {
  name: string;
  email: string;
  role: UserRole;
};

export const usersApi = {
  me:   () => request<User>("/users/me"),
  list: () => request<{ data: User[]; total: number }>("/users"),
  create: (body: NewUser) =>
    request<User>("/users", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: Partial<NewUser>) =>
    request<User>(`/users/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  remove: (id: string) => request<{ ok: boolean }>(`/users/${id}`, { method: "DELETE" }),
};

// ── AI API ─────────────────────────────────────────────────────────────────

export const aiApi = {
  templateEdit: (body: AiTemplateEditRequest) =>
    request<AiTemplateEditResponse>("/ai/template-edit", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  templateGenerate: (body: AiTemplateGenerateRequest) =>
    request<AiTemplateGenerateResponse>("/ai/template-generate", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  templateFromImport: (body: AiTemplateImportRequest) =>
    request<AiTemplateImportResponse>("/ai/template-from-import", {
      method: "POST",
      body: JSON.stringify(body),
    }),
};
